import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertCircle } from "lucide-react";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import {
  setCfcData,
  setCurrentTab,
  setUserInitiatedNav,
} from "../../../store/slices/cfcSlice";
import FilePreview from "../../../components/common/FilePreview";
import pdf3 from "../../../assets/pdf/pdf3.pdf";
import { Separator } from "@/components/ui/separator";

const isValidFile = (file) =>
  file instanceof File || (typeof file === "string" && file.length > 0);

const cfcEligibilitySchema = z
  .object({
    is_spv: z.string().min(1, "SPV status is required"),
    spv_other_specify: z.string().optional(),
    eb_registered_on: z.string().min(1, "This field is required"),
    eb_registered_member_name: z.string().optional(),
    eb_bill_copy: z
      .any()
      .refine((file) => isValidFile(file), "EB Bill copy is required"),
    component_included_in_machinery_list: z
      .string()
      .min(1, "This field is required"),
    beneficiary: z.string().optional(),
    details_machinery: z.string().optional(),
    cost_machinery: z.string().optional(),
    invoice_date: z.string().optional(),
    invoice_copy: z.any().optional(),
    premises_type: z.string().min(1, "Premises Type is required"),
    rental_copy: z.any().optional(),
    lease_copy: z.any().optional(),
    has_other_schemes: z.string().min(1, "This field is required"),
    other_state_gov_scheme: z.string().optional(),
    other_scheme_details: z.any().optional(),
    scheme_documents: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.is_spv === "Any Other (Specify)" &&
      (!data.spv_other_specify || data.spv_other_specify.trim() === "")
    ) {
      ctx.addIssue({
        path: ["spv_other_specify"],
        code: z.ZodIssueCode.custom,
        message: "Please specify the SPV type",
      });
    }

    if (data.eb_registered_on === "Member of the SPV") {
      if (!data.eb_registered_member_name) {
        ctx.addIssue({
          path: ["eb_registered_member_name"],
          code: z.ZodIssueCode.custom,
          message: "Registered member name is required",
        });
      }
    }

    if (data.component_included_in_machinery_list === "yes") {
      if (!data.details_machinery || data.details_machinery.trim() === "") {
        ctx.addIssue({
          path: ["details_machinery"],
          code: z.ZodIssueCode.custom,
          message: "Details of machinery are required",
        });
      }

      if (
        !data.cost_machinery ||
        data.cost_machinery.toString().trim() === ""
      ) {
        ctx.addIssue({
          path: ["cost_machinery"],
          code: z.ZodIssueCode.custom,
          message: "Cost of machinery is required",
        });
      }

      if (!data.invoice_date || data.invoice_date.trim() === "") {
        ctx.addIssue({
          path: ["invoice_date"],
          code: z.ZodIssueCode.custom,
          message: "Invoice date is required",
        });
      }

      const hasUpCopyFile = data.invoice_copy instanceof File;
      const hasUpCopyUrl =
        typeof data.invoice_copy === "string" && data.invoice_copy.length > 0;
      if (!hasUpCopyFile && !hasUpCopyUrl) {
        ctx.addIssue({
          path: ["invoice_copy"],
          code: z.ZodIssueCode.custom,
          message: "Upload documents are required",
        });
      }
    }

    if (data.premises_type === "Rented Premises") {
      const hasRentalFile = data.rental_copy instanceof File;
      const hasRentalUrl =
        typeof data.rental_copy === "string" && data.rental_copy.length > 0;
      if (!hasRentalFile && !hasRentalUrl) {
        ctx.addIssue({
          path: ["rental_copy"],
          code: z.ZodIssueCode.custom,
          message: "Rental agreement is required for rented premises",
        });
      }
    }

    if (data.has_other_schemes == "yes") {
      if (!data.other_state_gov_scheme) {
        ctx.addIssue({
          path: ["other_scheme_details"],
          code: z.ZodIssueCode.custom,
          message: "Specify Scheme Details is required",
        });
      }

      const hasSchemeFile = data.scheme_documents instanceof File;
      const hasSchemeUrl =
        typeof data.scheme_documents === "string" &&
        data.scheme_documents.length > 0;
      if (!hasSchemeFile && !hasSchemeUrl) {
        ctx.addIssue({
          path: ["scheme_documents"],
          code: z.ZodIssueCode.custom,
          message: "Scheme documents are required",
        });
      }
    }
  });

const EligibilityDetails = ({ data }) => {
  const [filePreviews, setFilePreviews] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonType, setButtonType] = useState("submit");
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(cfcEligibilitySchema),
    defaultValues: {
      is_spv: data?.beneficiary?.is_spv ?? "",
      spv_other_specify: data?.beneficiary?.spv_other_specify ?? "",
      eb_registered_on: data?.beneficiary?.eb_registered_on ?? "",
      eb_registered_member_name:
        data?.beneficiary?.eb_registered_member_name ?? "",
      component_included_in_machinery_list:
        data?.beneficiary?.component_included_in_machinery_list ?? "",
      beneficiary: data?.beneficiary?.beneficiary ?? "",
      details_machinery: data?.beneficiary?.details_machinery ?? "",
      cost_machinery: data?.beneficiary?.cost_machinery ?? "",
      other_scheme_details: data?.beneficiary?.other_scheme_details ?? "",
      invoice_date: data?.beneficiary?.invoice_date ?? "",
      premises_type: data?.beneficiary?.premises_type ?? "",
      has_other_schemes: data?.beneficiary?.has_other_schemes ?? "",
      other_state_gov_scheme: data?.beneficiary?.other_state_gov_scheme ?? "",
      rental_copy: data?.beneficiary?.rental_copy ?? "",
      lease_copy: data?.beneficiary?.lease_copy ?? "",
      scheme_documents: data?.beneficiary?.scheme_documents ?? "",
      eb_bill_copy: data?.beneficiary?.eb_bill_copy ?? "",
      invoice_copy: data?.beneficiary?.invoice_copy ?? "",
    },
  });

  const formData = watch();

  useEffect(() => {
    if (data?.beneficiary) {
      const previews = {
        rental_copy: data?.beneficiary?.rental_copy ?? "",
        lease_copy: data?.beneficiary?.lease_copy ?? "",
        scheme_documents: data?.beneficiary?.scheme_documents ?? "",
        eb_bill_copy: data?.beneficiary?.eb_bill_copy ?? "",
        invoice_copy: data?.beneficiary?.invoice_copy ?? "",
      };
      setFilePreviews(previews);
    }
  }, [data]);

  const handleFileUpload = async (name, file) => {
    if (file) {
      setValue(name, file, { shouldValidate: true });

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews((prev) => ({ ...prev, [name]: e.target.result }));
        };
        reader.readAsDataURL(file);
      }

      await trigger(name);
    }
  };

  const handleRemoveFile = (fieldName) => {
    setValue(fieldName, undefined, { shouldValidate: true });
    setFilePreviews((prev) => ({ ...prev, [fieldName]: null }));
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const submitData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (
          formData[key] !== undefined &&
          formData[key] !== null &&
          formData[key] !== ""
        ) {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          } else {
            submitData.append(key, formData[key]);
          }
        }
      });

      submitData.append("id", data?.id || "");

      let response;

      response = await api.post("/cfc-subsidies/eligibility", submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      dispatch(setCfcData(response?.data?.data || {}));

      toast({
        title: "Success!",
        description: "Application Saved Successfully",
        variant: "success",
      });

      dispatch(setUserInitiatedNav(true));
      if (buttonType === "next" && setCurrentTab) {
        setTimeout(() => {
          dispatch(setCurrentTab("bank"));
        }, 300);
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = async (fieldName, value) => {
    setValue(fieldName, value, { shouldValidate: true });
  };

  const onSubmitClick = async (e, type) => {
    e.preventDefault();
    setButtonType(type); // Save button type first
    console.log("🖱️ Submit button clicked. Type:", type);

    const isValid = await trigger();
    console.log("✅ Validation result:", isValid);
    console.log("⚠️ Validation errors:", errors);

    if (isValid) {
      console.log("🎯 Form is valid, calling handleSubmitSpv...");
      // await form.handleSubmit(handleSubmitSpv)();
    } else {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        variant: "destructive",
      });
    }
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Beneficiary Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {/* SPV Status */}
            <div className="space-y-2">
              <Label htmlFor="is_spv">
                Type of SPV <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.is_spv || ""}
                onValueChange={(value) => handleInputChange("is_spv", value)}
              >
                <SelectTrigger
                  className={errors.is_spv ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Self Help Group">
                    Self Help Group
                  </SelectItem>
                  <SelectItem value="Joint Venture">Joint Venture</SelectItem>
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="PartnerShip">PartnerShip</SelectItem>
                  <SelectItem value="Proprietorship">Proprietorship</SelectItem>
                  <SelectItem value="Any Other (Specify)">
                    Any Other (Specify)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.is_spv === "Any Other (Specify)" && (
              <div className="space-y-2 mt-2">
                <Label htmlFor="spv_other_specify">
                  Specify the type of SPV{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="spv_other_specify"
                  type="text"
                  placeholder="Enter SPV type"
                  value={formData.spv_other_specify || ""}
                  onChange={(e) =>
                    handleInputChange("spv_other_specify", e.target.value)
                  }
                  className={errors.spv_other_specify ? "border-red-500" : ""}
                />
                {errors.spv_other_specify && (
                  <p className="text-sm text-red-500">
                    {errors.spv_other_specify.message}
                  </p>
                )}
              </div>
            )}
          </div>
          <Separator />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="eb_registered_on" required>
                EB in the Name of
              </Label>
              <Select
                value={formData.eb_registered_on || ""}
                onValueChange={(value) =>
                  handleInputChange("eb_registered_on", value)
                }
              >
                <SelectTrigger
                  className={errors.eb_registered_on ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SPV">SPV</SelectItem>
                  <SelectItem value="Member of the SPV">
                    Member of the SPV
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.eb_registered_on && (
                <p className="text-sm text-red-500">
                  {errors.eb_registered_on.message}
                </p>
              )}
            </div>

            {formData.eb_registered_on === "Member of the SPV" && (
              <div className="space-y-2">
                <Label htmlFor="eb_registered_member_name" required>
                  Name of the Member of the SPV
                </Label>
                <Input
                  id="eb_registered_member_name"
                  value={formData.eb_registered_member_name || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "eb_registered_member_name",
                      e.target.value
                    )
                  }
                  className={
                    errors.eb_registered_member_name ? "border-red-500" : ""
                  }
                  placeholder="Enter owner name as per electricity bill"
                />
                {errors.eb_registered_member_name && (
                  <p className="text-sm text-red-500">
                    {errors.eb_registered_member_name.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="eb_bill_copy">
                Upload EB Bill <span className="text-red-500">*</span>
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="eb_bill_copy"
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    handleFileUpload("eb_bill_copy", e.target.files[0])
                  }
                  className={errors.eb_bill_copy ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="eb_bill_copy"
                  preview={filePreviews.eb_bill_copy}
                  file={
                    formData.eb_bill_copy instanceof File
                      ? formData.eb_bill_copy
                      : null
                  }
                  existingFile={
                    typeof filePreviews.eb_bill_copy === "string"
                      ? filePreviews.eb_bill_copy
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.eb_bill_copy && (
                  <p className="text-sm text-red-500">
                    {errors.eb_bill_copy.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF Files max size 2MB
                </p>
              </div>
            </div>
          </div>
          <Separator />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="component_included_in_machinery_list" required>
                Do the SPV have purchased machinery which is / are included in
                the machinery list MC-1B-Weaving Preparatory S.No.08 of GOI.
                <Button
                  type="button"
                  variant="link"
                  onClick={() => handleDownload(pdf3, `machinery.pdf`)}
                >
                  (Download Document)
                </Button>
              </Label>
              <Select
                value={formData.component_included_in_machinery_list || ""}
                onValueChange={(value) =>
                  handleInputChange(
                    "component_included_in_machinery_list",
                    value
                  )
                }
              >
                <SelectTrigger
                  className={
                    errors.component_included_in_machinery_list
                      ? "border-red-500"
                      : ""
                  }
                >
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.component_included_in_machinery_list && (
                <p className="text-sm text-red-500">
                  {errors.component_included_in_machinery_list.message}
                </p>
              )}
            </div>

            {formData.component_included_in_machinery_list === "yes" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="details_machinery">
                    Details of Machinery <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="details_machinery"
                    value={formData.details_machinery || ""}
                    onChange={(e) =>
                      handleInputChange("details_machinery", e.target.value)
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cost_machinery">
                    Cost of Machinery <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="cost_machinery"
                    value={formData.cost_machinery || ""}
                    onChange={(e) =>
                      handleInputChange("cost_machinery", e.target.value)
                    }
                    className={errors?.cost_machinery ? "border-red-500" : ""}
                  />
                  {errors?.cost_machinery && (
                    <p className="text-sm text-red-500">
                      {errors.cost_machinery}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invoice_date">
                    Invoice Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="invoice_date"
                    type="date"
                    value={formData.invoice_date || ""}
                    onChange={(e) =>
                      handleInputChange("invoice_date", e.target.value)
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invoice_copy">
                    Upload Invoice Document{" "}
                    <span className="text-red-500">*</span>
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <Input
                      id="invoice_copy"
                      type="file"
                      accept=".pdf"
                      onChange={(e) =>
                        handleFileUpload("invoice_copy", e.target.files[0])
                      }
                      className={errors.invoice_copy ? "border-red-500" : ""}
                    />

                    <FilePreview
                      fieldName="invoice_copy"
                      preview={filePreviews.invoice_copy}
                      file={
                        formData.invoice_copy instanceof File
                          ? formData.invoice_copy
                          : null
                      }
                      existingFile={
                        typeof filePreviews.invoice_copy === "string"
                          ? filePreviews.invoice_copy
                          : null
                      }
                      onRemove={handleRemoveFile}
                    />

                    {errors.invoice_copy && (
                      <p className="text-sm text-red-500">
                        {errors.invoice_copy.message}
                      </p>
                    )}
                    <p className="ms-1 text-[12px] text-muted-foreground">
                      Supported only PDF Files max size 2MB
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
          <Separator />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="premises_type">
                Premises Type
                <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.premises_type || ""}
                onValueChange={(value) =>
                  handleInputChange("premises_type", value)
                }
              >
                <SelectTrigger
                  className={errors.premises_type ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select premises type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Own Premises">Own Premises</SelectItem>
                  <SelectItem value="Rented Premises">
                    Rented Premises
                  </SelectItem>
                  <SelectItem value="Lease Premises">Lease Premises</SelectItem>
                </SelectContent>
              </Select>
              {errors.premises_type && (
                <p className="text-sm text-red-500">
                  {errors.premises_type.message}
                </p>
              )}
            </div>

            {formData.premises_type === "Rented Premises" && (
              <div className="space-y-2">
                <Label htmlFor="rental_copy" required>
                  Rental Agreement
                </Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  <Input
                    id="rental_copy"
                    type="file"
                    accept=".pdf"
                    onChange={(e) =>
                      handleFileUpload("rental_copy", e.target.files[0])
                    }
                    className={errors.rental_copy ? "border-red-500" : ""}
                  />

                  <FilePreview
                    fieldName="rental_copy"
                    preview={filePreviews.rental_copy}
                    file={
                      formData.rental_copy instanceof File
                        ? formData.rental_copy
                        : null
                    }
                    existingFile={
                      typeof filePreviews.rental_copy === "string"
                        ? filePreviews.rental_copy
                        : null
                    }
                    onRemove={handleRemoveFile}
                  />

                  {errors.rental_copy && (
                    <p className="text-sm text-red-500">
                      {errors.rental_copy.message}
                    </p>
                  )}
                  <p className="ms-1 text-[12px] text-muted-foreground">
                    Supported only PDF Files max size 2MB
                  </p>
                </div>
              </div>
            )}
            {formData.premises_type === "Lease Premises" && (
              <div className="space-y-2">
                <Label htmlFor="lease_copy" required>
                  Lease Agreement
                </Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  <Input
                    id="lease_copy"
                    type="file"
                    accept=".pdf"
                    onChange={(e) =>
                      handleFileUpload("lease_copy", e.target.files[0])
                    }
                    className={errors.lease_copy ? "border-red-500" : ""}
                  />

                  <FilePreview
                    fieldName="lease_copy"
                    preview={filePreviews.lease_copy}
                    file={
                      formData.lease_copy instanceof File
                        ? formData.lease_copy
                        : null
                    }
                    existingFile={
                      typeof filePreviews.lease_copy === "string"
                        ? filePreviews.lease_copy
                        : null
                    }
                    onRemove={handleRemoveFile}
                  />

                  {errors.lease_copy && (
                    <p className="text-sm text-red-500">
                      {errors.lease_copy.message}
                    </p>
                  )}
                  <p className="ms-1 text-[12px] text-muted-foreground">
                    Supported only PDF Files max size 2MB
                  </p>
                </div>
              </div>
            )}
          </div>
          <Separator />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="has_other_schemes" required>
                Receiving Benefits under Other Schemes?
              </Label>
              <Select
                value={formData.has_other_schemes || ""}
                onValueChange={(value) => {
                  handleInputChange("has_other_schemes", value);
                }}
              >
                <SelectTrigger
                  className={errors.has_other_schemes ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.has_other_schemes && (
                <p className="text-sm text-red-500">
                  {errors.has_other_schemes.message}
                </p>
              )}
            </div>

            {formData.has_other_schemes == "yes" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="other_state_gov_scheme" required>
                    Other State Government Schemes
                  </Label>
                  <Select
                    value={formData.other_state_gov_scheme || ""}
                    onValueChange={(value) =>
                      handleInputChange("other_state_gov_scheme", value)
                    }
                  >
                    <SelectTrigger
                      className={
                        errors.other_state_gov_scheme ? "border-red-500" : ""
                      }
                    >
                      <SelectValue placeholder="Select scheme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SSI / MSME">SSI / MSME</SelectItem>
                      <SelectItem value="Industries Department">
                        Industries Department
                      </SelectItem>
                      <SelectItem value="Textile Department">
                        Textile Department
                      </SelectItem>
                      <SelectItem value="Any Other Scheme">
                        Any Other Scheme
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.other_state_gov_scheme && (
                    <p className="text-sm text-red-500">
                      {errors.other_state_gov_scheme.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="other_scheme_details" required>
                    Specify Scheme Details{" "}
                  </Label>
                  <Input
                    id="other_scheme_details"
                    {...register("other_scheme_details")}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange("other_scheme_details", value);
                    }}
                    className={
                      errors.other_scheme_details ? "border-red-500" : ""
                    }
                  />
                  {errors.other_scheme_details && (
                    <p className="text-sm text-red-500">
                      {errors.other_scheme_details.message}
                    </p>
                  )}
                </div>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  <Label
                    htmlFor="other_scheme_details"
                    className="text-left"
                    required
                  >
                    Upload scheme Document{" "}
                  </Label>
                  <Input
                    id="scheme_documents"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) =>
                      handleFileUpload("scheme_documents", e.target.files[0])
                    }
                    className={errors.scheme_documents ? "border-red-500" : ""}
                  />

                  <FilePreview
                    fieldName="scheme_documents"
                    preview={filePreviews.scheme_documents}
                    file={
                      formData.scheme_documents instanceof File
                        ? formData.scheme_documents
                        : null
                    }
                    existingFile={
                      typeof filePreviews.scheme_documents === "string"
                        ? filePreviews.scheme_documents
                        : null
                    }
                    onRemove={handleRemoveFile}
                  />

                  {errors.scheme_documents && (
                    <p className="text-sm text-red-500">
                      {errors.scheme_documents.message}
                    </p>
                  )}

                  <p className="ms-1 text-[12px] text-muted-foreground">
                    Supported only JPG, JPEG, PNG, PDF Files max size 2MB
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end mt-6 gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => setButtonType("submit")}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={() => setButtonType("next")}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default EligibilityDetails;
